/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { prepScopes } from '../utils/GraphHelpers';
import { findPeople } from './graph.people';
/**
 * async promise, returns Graph User data relating to the user logged in
 *
 * @returns {(Promise<User>)}
 * @memberof Graph
 */
export function getMe(graph) {
    return graph
        .api('me')
        .middlewareOptions(prepScopes('user.read'))
        .get();
}
/**
 * asnyc promise, returns IDynamicPerson
 *
 * @param {string} userId
 * @returns {(Promise<IDynamicPerson>)}
 * @memberof Graph
 */
export function getUserWithPhoto(graph, userId) {
    return __awaiter(this, void 0, void 0, function* () {
        const batch = graph.createBatch();
        let person = null;
        if (userId) {
            batch.get('user', `/users/${userId}`, ['user.readbasic.all']);
            batch.get('photo', `users/${userId}/photo/$value`, ['user.readbasic.all']);
        }
        else {
            batch.get('user', 'me', ['user.read']);
            batch.get('photo', 'me/photo/$value', ['user.read']);
        }
        const response = yield batch.execute();
        if (response && response.user) {
            person = response.user;
            person.personImage = response.photo;
        }
        return person;
    });
}
/**
 * async promise, returns all Graph users associated with the userPrincipleName provided
 *
 * @param {string} userPrincipleName
 * @returns {(Promise<User>)}
 * @memberof Graph
 */
export function getUser(graph, userPrincipleName) {
    const scopes = 'user.readbasic.all';
    return graph
        .api(`/users/${userPrincipleName}`)
        .middlewareOptions(prepScopes(scopes))
        .get();
}
/**
 * Returns a Promise of Graph Users array associated with the user ids array
 *
 * @export
 * @param {IGraph} graph
 * @param {string[]} userIds, an array of string ids
 * @returns {Promise<User[]>}
 */
export function getUsersForUserIds(graph, userIds) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!userIds || userIds.length === 0) {
            return [];
        }
        const batch = graph.createBatch();
        for (const id of userIds) {
            if (id !== '') {
                batch.get(id, `/users/${id}`, ['user.readbasic.all']);
            }
        }
        try {
            const response = yield batch.execute();
            const people = [];
            // iterate over userIds to ensure the order of ids
            for (const id of userIds) {
                const person = response[id];
                if (person && person.id) {
                    people.push(person);
                }
            }
            return people;
        }
        catch (_) {
            // fallback to making the request one by one
            try {
                return Promise.all(userIds.filter(id => id && id !== '').map(id => getUser(graph, id)));
            }
            catch (_) {
                return [];
            }
        }
    });
}
/**
 * Returns a Promise of Graph Users array associated with the people queries array
 *
 * @export
 * @param {IGraph} graph
 * @param {string[]} peopleQueries, an array of string ids
 * @returns {Promise<User[]>}
 */
export function getUsersForPeopleQueries(graph, peopleQueries) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!peopleQueries || peopleQueries.length === 0) {
            return [];
        }
        const batch = graph.createBatch();
        const people = [];
        for (const personQuery of peopleQueries) {
            if (personQuery !== '') {
                batch.get(personQuery, `/me/people?$search="${personQuery}"`, ['people.read']);
            }
        }
        try {
            const response = yield batch.execute();
            for (const personQuery of peopleQueries) {
                const person = response[personQuery];
                if (person && person.value && person.value.length) {
                    people.push(person.value[0]);
                }
            }
            return people;
        }
        catch (_) {
            try {
                return Promise.all(peopleQueries
                    .filter(personQuery => personQuery && personQuery !== '')
                    .map((personQuery) => __awaiter(this, void 0, void 0, function* () {
                    const personArray = yield findPeople(graph, personQuery, 1);
                    if (personArray && personArray.length) {
                        return personArray[0];
                    }
                })));
            }
            catch (_) {
                return [];
            }
        }
    });
}
/**
 * Search Microsoft Graph for Users in the organization
 *
 * @export
 * @param {IGraph} graph
 * @param {string} query - the string to search for
 * @param {number} [top=10] - maximum number of results to return
 * @returns {Promise<User[]>}
 */
export function findUsers(graph, query, top = 10) {
    return __awaiter(this, void 0, void 0, function* () {
        const scopes = 'User.ReadBasic.All';
        const result = yield graph
            .api('users')
            .filter(`startswith(displayName,'${query}') or startswith(givenName,'${query}') or startswith(surname,'${query}') or startswith(mail,'${query}') or startswith(userPrincipalName,'${query}')`)
            .top(top)
            .middlewareOptions(prepScopes(scopes))
            .get();
        return result ? result.value : null;
    });
}
//# sourceMappingURL=graph.user.js.map